import { useState, useEffect, useMemo } from 'react';
import * as styles from '@/components/organisms/Learning/LearningSytle.css'
import clsx from 'clsx'
import { Button } from '@/components/atoms';
import { getStudentActivityTitle } from '@/utils/activityTitle';

interface FreeWritngProps {
  activity?: any;
  unitData?: any;
  onResponse?: (response: any) => void;
  existingResponse?: any;
  isHomework?: boolean;
}

const DEFAULT_LINE_COUNT = 6;
const MIN_LINE_COUNT = 1;
const MAX_LINE_COUNT = 10;

const getByteLength = (value: string) => {
    let len = 0;
    for (let i = 0; i < value.length; i++) {
        len += value.charCodeAt(i) > 127 ? 2 : 1;
    }
    return len;
};

const normalizeLineCount = (value: any): number => {
    const numeric = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(numeric)) {
        return DEFAULT_LINE_COUNT;
    }
    const rounded = Math.round(numeric);
    return Math.min(MAX_LINE_COUNT, Math.max(MIN_LINE_COUNT, rounded));
};

const tokenizeEnglishWords = (value: string) => (
    value.toLowerCase().match(/[a-z0-9']+/g) ?? []
);

const buildHintTokenSet = (hints: string[]) => {
    const tokens = new Set<string>();
    hints.forEach((hint) => {
        tokenizeEnglishWords(hint).forEach((token) => tokens.add(token));
    });
    return tokens;
};

const isOnlyHintTokens = (text: string, hintTokens: Set<string>) => {
    const tokens = tokenizeEnglishWords(text);
    if (tokens.length === 0) return false;
    return tokens.every((token) => hintTokens.has(token));
};

export default function FreeWritng({ activity, unitData, onResponse, existingResponse, isHomework = false }: FreeWritngProps) {
    console.log('=== FreeWritng 시작 ===');
    console.log('Activity:', activity);
    console.log('isHomework:', isHomework);
    
    // configJson 먼저 확인 (ESSAY 타입)
    let activityConfig: any = {};
    try {
        if (activity?.configJson) {
            activityConfig = typeof activity.configJson === 'string'
                ? JSON.parse(activity.configJson)
                : activity.configJson;
        }
    } catch (e) {
        console.error('configJson 파싱 에러:', e);
    }
    
    // configJson에 데이터가 있으면 사용, 없으면 items에서
    const hasConfig = activityConfig.hint || activityConfig.keyword;
    let content: any = {};
    
    if (!hasConfig && activity?.items?.[0]) {
        const activityData = activity.items[0];
        try {
            content = activityData.contentJson ? JSON.parse(activityData.contentJson) : {};
        } catch (e) {
            console.error('contentJson 파싱 에러:', e);
        }
    }

    const topic = activityConfig.keyword || activityConfig.topic || content.subject || "제시어 : 과거 일반 동사";
    const hintsSource = activityConfig.hint || content.hints || 'went, ruined, planted, bought, threw, belived';
    const rawHints = Array.isArray(hintsSource)
        ? hintsSource
        : (typeof hintsSource === 'string' ? hintsSource.split(/[,\n]/) : []);
    const lineCountSource =
        activityConfig.writingCount ??
        content.lineCount ??
        content.lines ??
        DEFAULT_LINE_COUNT;
    const lineCount = normalizeLineCount(lineCountSource);
    const hintWords = rawHints
        .map((h: any) => String(h).trim())
        .filter(Boolean)
        .slice(0, lineCount);
    const hintTokenSet = useMemo(() => buildHintTokenSet(hintWords), [hintWords]);
    const title = getStudentActivityTitle(activity?.title, '프리라이팅');
    
    console.log('=== FreeWritng 데이터 파싱 ===');
    console.log('activityConfig:', activityConfig);
    console.log('activityConfig.writingCount:', activityConfig.writingCount);
    console.log('content:', content);
    console.log('Topic:', topic);
    console.log('Hints:', hintWords);
    console.log('Line Count (최종):', lineCount);

    // 힌트 배열 (입력 검증용)
    // 입력 필드 관리
    const [inputTexts, setInputTexts] = useState<string[]>(() => {
        console.log('inputTexts 초기화, lineCount:', lineCount);
        
        // existingResponse가 있더라도 lineCount가 다르면 lineCount에 맞춤
        if (existingResponse?.inputTexts) {
            const existingLength = existingResponse.inputTexts.length;
            console.log('existingResponse.inputTexts 길이:', existingLength);
            
            if (existingLength !== lineCount) {
                console.log('⚠️ 길이 불일치! existingResponse 무시하고 lineCount 사용:', lineCount);
                return Array(lineCount).fill('');
            }
            
            return existingResponse.inputTexts;
        }
        
        return Array(lineCount).fill('');
    }); 
    // 각 인풋 줄마다 검증 오류 유무 및 에러 메세지 
    const [errors, setErrors] = useState<string[]>(() => {
        if (existingResponse?.errors && existingResponse.errors.length === lineCount) {
            return existingResponse.errors;
        }
        return Array(lineCount).fill('');
    });
    // 각 줄마다 확인 버튼 클릭 여부
    const [checkedLines, setCheckedLines] = useState<boolean[]>(() => {
        if (existingResponse?.checkedLines && existingResponse.checkedLines.length === lineCount) {
            return existingResponse.checkedLines;
        }
        return Array(lineCount).fill(false);
    });

    // lineCount가 변경될 때 배열 크기 조정
    useEffect(() => {
        console.log('lineCount 변경 감지:', lineCount);
        console.log('현재 inputTexts 길이:', inputTexts.length);
        
        // 길이가 맞지 않으면 항상 조정 (existingResponse 여부 무관)
        if (inputTexts.length !== lineCount) {
            console.log('배열 크기 조정:', inputTexts.length, '->', lineCount);
            setInputTexts(Array(lineCount).fill(''));
            setErrors(Array(lineCount).fill(''));
            setCheckedLines(Array(lineCount).fill(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lineCount]);

    // 모든 조건이 만족되었는지 체크
    const checkAllConditions = (texts: string[], errs: string[], checked: boolean[], options?: { forceHomeworkEval?: boolean }) => {
        // 숙제 모드는 항상 true (단, 강제 평가 옵션 제외)
        if (isHomework && !options?.forceHomeworkEval) {
            return true;
        }
        
        // 학습 모드: 힌트 개수만큼의 문장이 작성되고 확인되어야 함
        const requiredHintsCount = Math.min(hintWords.length, lineCount); // 입력 줄 수 이상 요구하지 않도록 제한
        
        // 작성된 줄들 (입력이 있는 줄만)
        const filledIndices = texts.map((t, idx) => ({ text: t, idx }))
                                   .filter(item => item.text.trim() !== '');
        
        // 작성된 줄의 개수가 힌트 개수 이상이어야 함
        const hasEnoughInput = filledIndices.length >= requiredHintsCount;
        
        // 작성된 줄들이 모두 확인되었는지
        const allFilledLinesChecked = filledIndices.every(item => checked[item.idx] === true);
        
        // 작성된 줄들에 에러가 없는지
        const noErrorsInFilledLines = filledIndices.every(item => errs[item.idx] === '');
        
        // 힌트 개수만큼의 서로 다른 힌트 단어가 사용되어야 함
        const usageCounts = countHintWordUsage(texts);
        const usedHintsCount = Object.values(usageCounts).filter((count: any) => count >= 1).length;
        
        console.log('=== 조건 체크 ===');
        console.log('입력칸 개수:', lineCount);
        console.log('필요한 힌트 개수:', requiredHintsCount);
        console.log('작성된 문장 개수:', filledIndices.length);
        console.log('충분한 입력:', hasEnoughInput);
        console.log('작성된 줄 모두 확인됨:', allFilledLinesChecked);
        console.log('작성된 줄 에러 없음:', noErrorsInFilledLines);
        console.log('사용된 힌트 개수:', usedHintsCount, '/ 필요:', requiredHintsCount);
        console.log('사용 횟수:', usageCounts);
        
        return hasEnoughInput && allFilledLinesChecked && noErrorsInFilledLines && (usedHintsCount >= requiredHintsCount);
    };

    const computeHomeworkReady = (texts: string[], errs: string[], checked: boolean[]) => {
        if (isHomework) {
            return texts.every((text) => getByteLength(text.trim()) >= 1);
        }
        return checkAllConditions(texts, errs, checked, { forceHomeworkEval: true });
    };

    // 마운트 시 초기 응답 저장 또는 기존 응답 복원
    useEffect(() => {
        if (onResponse) {
            if (existingResponse) {
                // 기존 응답이 있으면 isCorrect 재계산
                const existingCheckedLines = existingResponse.checkedLines || checkedLines;
                const isCorrect = checkAllConditions(
                    existingResponse.inputTexts || inputTexts,
                    existingResponse.errors || errors,
                    existingCheckedLines
                );
                onResponse({
                    ...existingResponse,
                    checkedLines: existingCheckedLines,
                    isCorrect: isCorrect,
                    isHomeworkReady: computeHomeworkReady(
                        existingResponse.inputTexts || inputTexts,
                        existingResponse.errors || errors,
                        existingCheckedLines
                    )
                });
            } else {
                // 새로 시작
                onResponse({
                    inputTexts: Array(lineCount).fill(''),
                    errors: Array(lineCount).fill(''),
                    checkedLines: Array(lineCount).fill(false),
                    isCorrect: isHomework ? true : false,
                    isHomeworkReady: false
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const countHintWordUsage = (texts: string[]) => {
        const counts: Record<string, number> = {};
        const lowerTexts = texts.map(t => t.toLowerCase());
      
        hintWords.forEach((word: string) => {
          counts[word] = 0;
          lowerTexts.forEach((text) => {
            if (text.includes(word)) {
              counts[word]++;
            }
          });
        });
        return counts;
      };

    const handleChange = (text: string, idx: number) => {
        const newTexts = [...inputTexts];
        newTexts[idx] = text;
        setInputTexts(newTexts);
        
        // 입력 내용이 변경되면 해당 줄의 확인 상태와 에러 초기화
        const newCheckedLines = [...checkedLines];
        const newErrors = [...errors];
        newCheckedLines[idx] = false; // 수정하면 다시 확인 필요
        newErrors[idx] = ''; // 에러 메시지 초기화
        setCheckedLines(newCheckedLines);
        setErrors(newErrors);
        
        // 숙제 모드일 때는 검증하지 않음
        if (isHomework) {
            if (onResponse) {
                const emptyErrors = Array(lineCount).fill('');
                onResponse({
                    inputTexts: newTexts,
                    errors: emptyErrors,
                    isCorrect: true,
                    isHomeworkReady: computeHomeworkReady(newTexts, emptyErrors, newCheckedLines)
                });
            }
            return;
        }
        
        // 학습 모드: 확인 버튼을 누르기 전까지는 에러 표시하지 않음
        // 응답 저장만 함
        const isCorrect = checkAllConditions(newTexts, newErrors, newCheckedLines);
        if (onResponse) {
            onResponse({
                inputTexts: newTexts,
                errors: newErrors,
                checkedLines: newCheckedLines,
                isCorrect: isCorrect,
                isHomeworkReady: computeHomeworkReady(newTexts, newErrors, newCheckedLines)
            });
        }
    };
    
    // 확인 버튼 클릭 핸들러
    const handleCheck = (idx: number) => {
        // 숙제 모드일 때는 검증하지 않음
        if (isHomework) {
            return;
        }
        
        const newErrors = [...errors];
        const newCheckedLines = [...checkedLines];
        const lowerText = inputTexts[idx].toLowerCase();
        
        // 입력이 없으면 확인 불가 (에러 메시지 표시)
        if (lowerText.trim() === '') {
            newErrors[idx] = '문장을 입력해주세요!';
            newCheckedLines[idx] = false; // 입력이 없으면 확인 안 됨
            setErrors(newErrors);
            setCheckedLines(newCheckedLines);
            
            const isCorrect = checkAllConditions(inputTexts, newErrors, newCheckedLines);
            if (onResponse) {
                onResponse({
                    inputTexts: inputTexts,
                    errors: newErrors,
                    checkedLines: newCheckedLines,
                    isCorrect: isCorrect
                });
            }
            return;
        }
        
        const usageCounts = countHintWordUsage(inputTexts);
        const usedInThisText = hintWords.filter((word: string) => lowerText.includes(word));
        const onlyHintTokens = isOnlyHintTokens(lowerText, hintTokenSet);
        
        let errorMessage = '';
        
        // 힌트 단어 아예 없으면
        if (usedInThisText.length === 0) {
            errorMessage = 'Hint에 제시된 단어를 사용해주세요!';
        } else if (onlyHintTokens) {
            errorMessage = '힌트 단어만 쓰면 안돼요! 문장으로 작성해주세요.';
        } else {
            const usedWord = usedInThisText[0];
            if (usageCounts[usedWord] > 1) {
                errorMessage = '같은 단어는 여러 문장에서 사용할 수 없어요!';
            }
        }
        
        newErrors[idx] = errorMessage;
        // 에러가 없을 때만 확인 완료로 처리
        newCheckedLines[idx] = errorMessage === '';
        setErrors(newErrors);
        setCheckedLines(newCheckedLines);
        
        // 모든 조건 체크 후 isCorrect 업데이트
        const isCorrect = checkAllConditions(inputTexts, newErrors, newCheckedLines);
        
        // 응답 저장
        if (onResponse) {
            onResponse({
                inputTexts: inputTexts,
                errors: newErrors,
                checkedLines: newCheckedLines,
                isCorrect: isCorrect,
                isHomeworkReady: computeHomeworkReady(inputTexts, newErrors, newCheckedLines)
            });
        }
    };
    
  return (
    <main>
     <div>
        <h1 className='px-5 pt-5 pb-3 heading1'>{title}</h1>
        <div className='p-5 pt-0'>
          <div className={styles.gameCard}>
            <div className='px-14 py-4'>
                <div className='relative'>
                    <p className='!text-2xl font-bold text-center break-words'>{topic}</p>
                    {/* <div className={styles.blockScreen}></div> */}
                </div>
            </div>
          </div>
            
            <section className='flex flex-col justify-center items-center p-5 mt-3 bg-gray-50 rounded-2xl border border-gray-100'>
                <span className='font-bold text-indigo-500'>Hint</span>                
                <div className='flex flex-wrap gap-y-1 gap-4 justify-center items-center py-2 text-2xl font-bold'>
                    {hintWords.map((word: string, idx: number) => (
                        <p key={idx}>{word}</p>
                    ))}
                </div>
            </section>
            {!isHomework && (
                <p className='mt-3 text-sm text-gray-500'>hint에 제시된 단어는 한 번만 작성 가능합니다.</p>
            )}
            <section className='flex flex-col gap-3 mt-4'>
            {inputTexts.map((inputText, idx) => (
    <div key={idx}>
      <div className="flex gap-3">
        <input
          value={inputText}
          onChange={(e) => handleChange(e.target.value, idx)}
          placeholder="문장을 입력 해주세요."
          type="text"
          className={clsx(styles.graInput, {
            '!border-red-500 !border-3': !isHomework && errors[idx],
            '!border-green-500 !border-3': !isHomework && checkedLines[idx] && !errors[idx],
          })}
        />
        {!isHomework && (
          <Button 
            className="flex-none w-20"
            onClick={() => handleCheck(idx)}
          >
            확인
          </Button>
        )}
      </div>

      {!isHomework && errors[idx] && (
        <small className="block mt-1 text-sm text-red-500">
          {errors[idx]}
        </small>
      )}
    </div>
  ))}
            </section>
                                                               
        </div>
     </div>
    </main>
  )
}